import { Box, Tooltip, Typography } from '@mui/material';
import EngagementMeter from '../EngagementMeter/EngagementMeter';
import NameCell from '../Table/NameCell';
import i18n from '../../i18n';
import { IClientObjective } from '../../redux/client/clientSlice';
import { transformEngagementData } from '../../utils/dateUtils';
import StatusSwitch from './StatusSwitch';
import ClientDropdownMenu, { OptionsEnum } from './ClientDropdownMenu';
import LetterAvatar from '../LetterAvatar/LetterAvatar';
import { hideScrollbarStyles } from '../../utils/styles';
import { ClientType } from '../../types/client';

const TooltipItem = ({ iconName, label }: { iconName: string; label: string }) => (
  <Box display="flex" gap="8px" justifyContent="flex-start" alignItems="center">
    <img src={`/assets/icons/${iconName}.svg`} alt={label} />
    <Typography variant="button" color="primary" textTransform="none" fontWeight={400}>
      {label}
    </Typography>
  </Box>
);

export const CustomTooltipContent = () => (
  <Box p="8px" display="flex" flexDirection="column" gap="6px">
    <TooltipItem iconName="good-engagement" label={i18n.t('clientTable.engagementLevels.good')} />
    <TooltipItem
      iconName="average-engagement"
      label={i18n.t('clientTable.engagementLevels.average')}
    />
    <TooltipItem iconName="low-engagement" label={i18n.t('clientTable.engagementLevels.low')} />
    <TooltipItem iconName="no-data" label={i18n.t('clientTable.engagementLevels.noData')} />
  </Box>
);

export interface IClientRow {
  id: string;
  avatar: string;
  engagementLevel: {
    date: string;
    engagementLevel: string;
  }[];
  lastActivity: string;
  name: string;
  objectives: string;
  team: {
    id: string;
    firstName: string;
    lastName: string;
    profilePic: string;
  }[];
  firstName: string;
  lastName: string;
  profilePic: string;
  email: string;
  profilePicture: string;
  isActive: boolean;
  clientType: ClientType;
}

interface IRenderCellParams {
  row: IClientRow;
  value: string | boolean | IClientObjective[];
}

export interface IColumn {
  field: string;
  headerName: string;
  hide?: boolean;
  width?: number;
  disableColumnMenu?: boolean;
  sortable?: boolean;
  flex?: number;
  renderCell?: (_params: IRenderCellParams) => React.ReactNode;
  renderHeader?: (_params: IRenderCellParams) => React.ReactNode;
}

const clientColumns: IColumn[] = [
  { field: 'id', headerName: 'ID', hide: true },
  {
    field: 'name',
    headerName: i18n.t('clientTable.name'),
    width: 500,
    disableColumnMenu: true,
    sortable: false,
    flex: 4,
    renderCell: (params: IRenderCellParams) => (
      <NameCell
        avatar={params.row.profilePic}
        firstName={params.row.firstName}
        lastName={params.row.lastName}
      />
    ),
    renderHeader: () => (
      <Box display="flex">
        <Typography variant="meta" color="primary">
          {i18n.t('clientTable.name')}
        </Typography>
      </Box>
    ),
  },
  {
    field: 'Assigned Team',
    headerName: i18n.t('clientTable.assignedTeam'),
    width: 200,
    disableColumnMenu: true,
    sortable: false,
    flex: 2,
    renderHeader: () => (
      <Box display="flex">
        <Typography variant="meta" color="primary">
          {i18n.t('clientTable.assignedTeam')}
        </Typography>
      </Box>
    ),
    renderCell: (params: IRenderCellParams) => (
      <Box
        display="flex"
        gap="4px"
        flexDirection="row"
        overflow="auto"
        sx={{ ...hideScrollbarStyles }}
      >
        <Tooltip
          title={params.row.team
            .map((member) => `${member.firstName} ${member.lastName}`)
            .join(', ')}
          placement="top"
          arrow
          componentsProps={{
            tooltip: {
              sx: {
                bgcolor: 'primary.main',
                borderRadius: '5px',
                padding: '8px 12px',
                textDecoration: 'title',
              },
            },
            arrow: {
              sx: {
                '&:before': {
                  bgcolor: 'primary.main',
                },
              },
            },
          }}
        >
          <Box
            display="flex"
            gap="4px"
            flexDirection="row"
            overflow="auto"
            sx={{ ...hideScrollbarStyles }}
          >
            {params.row.team.map((member) => (
              <Box key={member.id}>
                {member.profilePic ? (
                  <img
                    src={
                      member.profilePic?.startsWith('https')
                        ? member.profilePic
                        : `${process.env.REACT_APP_MEDIA_FILES_BASE_URL}${
                            member.profilePic?.startsWith('/') ? '' : '/'
                          }${member.profilePic}`
                    }
                    alt="user-avatar"
                    loading="lazy"
                    style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      objectFit: 'cover',
                    }}
                  />
                ) : (
                  <LetterAvatar firstName={member.firstName} lastName={member.lastName} />
                )}
              </Box>
            ))}
          </Box>
        </Tooltip>
      </Box>
    ),
  },
  {
    field: 'objectives',
    headerName: i18n.t('clientTable.objectives'),
    width: 150,
    disableColumnMenu: true,
    sortable: false,
    flex: 6,
    renderHeader: () => (
      <Box display="flex">
        <Typography variant="meta" color="primary">
          {i18n.t('clientTable.objectives')}
        </Typography>
      </Box>
    ),
    renderCell: (params: IRenderCellParams) => {
      if (Array.isArray(params.value)) {
        const content = params.value.map((obj: IClientObjective) => obj.objectiveName).join(', ');
        const displayContent = content.length > 80 ? `${content.substr(0, 80)}...` : content;

        return (
          <Tooltip
            title={content}
            placement="top"
            arrow
            componentsProps={{
              tooltip: {
                sx: {
                  bgcolor: 'primary.main',
                  borderRadius: '5px',
                  padding: '8px 12px',
                },
              },
              arrow: {
                sx: {
                  '&:before': {
                    bgcolor: 'primary.main',
                  },
                },
              },
            }}
          >
            <Box>
              <Typography variant="bodyRegular" color="primary">
                {displayContent}
              </Typography>
            </Box>
          </Tooltip>
        );
      }
      return null;
    },
  },
  {
    field: 'engagementLevel',
    headerName: i18n.t('clientTable.engagementLevel'),
    width: 400,
    disableColumnMenu: true,
    sortable: false,
    flex: 5,
    renderCell: (params: IRenderCellParams) => {
      const transformedEngageData = params.row.engagementLevel.map((data) => ({
        date: data.date,
        engagementLevel: data.engagementLevel as 'NONE' | 'PARTIAL' | 'FULL' | 'NO_DATA',
      }));

      return <EngagementMeter engageData={transformEngagementData(transformedEngageData)} />;
    },
    renderHeader: () => (
      <Box display="flex" gap="4px">
        <Typography variant="meta" color="primary">
          {i18n.t('clientTable.engagementLevel')}
        </Typography>
        <Tooltip
          title={<CustomTooltipContent />}
          placement="top"
          arrow
          componentsProps={{
            tooltip: {
              sx: {
                bgcolor: '#FFF',
                borderRadius: '5px',
                border: '1px solid #FEE4DB',
                boxShadow: '0px 4px 40px 0px rgba(255, 181, 171, 0.20)',
              },
            },
            arrow: {
              sx: {
                '&:before': {
                  bgcolor: '#FFF',
                  border: '1px solid #FEE4DB',
                  boxShadow: '0px 4px 40px 0px rgba(255, 181, 171, 0.20)',
                },
              },
            },
          }}
        >
          <img src="/assets/icons/info-circle.svg" alt="i-icon" />
        </Tooltip>
      </Box>
    ),
  },
];

export const activeClientsColumns = [
  ...clientColumns,
  {
    field: 'status',
    headerName: i18n.t('clientTable.status'),
    disableColumnMenu: true,
    sortable: false,
    flex: 2,
    renderHeader: () => (
      <Box display="flex">
        <Typography variant="meta" color="primary.main">
          {i18n.t('clientTable.status')}
        </Typography>
      </Box>
    ),
    renderCell: (params: IRenderCellParams) => (
      <StatusSwitch isActive={params.row.isActive} rowId={params.row.id} />
    ),
  },
  {
    field: 'actions',
    headerName: '',
    width: 120,
    disableColumnMenu: true,
    sortable: false,
    flex: 1.2,
    renderCell: (params: IRenderCellParams) => <ClientDropdownMenu row={params.row} />,
  },
];

export const archivedClientsColumns = [...clientColumns];
export const clientGroupColumns = [
  ...activeClientsColumns.slice(0, 2),
  ...activeClientsColumns.slice(4, 6),
  {
    field: 'actions',
    headerName: '',
    width: 120,
    disableColumnMenu: true,
    sortable: false,
    flex: 1.2,
    renderCell: (params: IRenderCellParams) => (
      <ClientDropdownMenu
        row={params.row}
        hideOptions={[OptionsEnum.ASSIGN_ACTIVITIES, OptionsEnum.VIEW_PROGRESS]}
      />
    ),
  },
];
